import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Check, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { PackageIncludedActsStep, type PackageActLimit } from "../components/PackageIncludedActsStep";
import { PackageValidationStep } from "../components/PackageValidationStep";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import {
  createAdminPackage,
  fetchAdminPackage,
  fetchAdminPackageHospitalActs,
  fetchAdminPackagesMeta,
  updateAdminPackage,
  type AdminPackageAct,
  type AdminPackageDetail,
  type AdminPackageMeta,
} from "../api/adminApi";
import { getAuthToken } from "../auth";
import { cn } from "@/shared/lib/cn";

type Step = 0 | 1 | 2;

type PackageFormState = {
  hospitalId: string;
  companyId: string;
  name: string;
  monthlyRate: string;
  periodStart: string;
  periodEnd: string;
  description: string;
  status: "draft" | "active" | "suspended";
};

function buildLimitsPayload(
  includedActIds: string[],
  actLimits: Record<string, PackageActLimit>,
): Record<number, { quantity?: number; period?: string; notes?: string }> {
  const limits: Record<number, { quantity?: number; period?: string; notes?: string }> = {};
  for (const actId of includedActIds) {
    const raw = actLimits[actId];
    if (!raw) continue;
    const qty = raw.quantity.trim() ? Number(raw.quantity) : undefined;
    const period = raw.period || undefined;
    const notes = raw.notes.trim() || undefined;
    if ((qty != null && qty > 0) || period || notes) {
      limits[Number(actId)] = {
        ...(qty != null && qty > 0 ? { quantity: qty } : {}),
        ...(period ? { period } : {}),
        ...(notes ? { notes } : {}),
      };
    }
  }
  return limits;
}

function limitsFromPackageActs(
  acts: AdminPackageDetail["acts"] | undefined,
): Record<string, PackageActLimit> {
  const out: Record<string, PackageActLimit> = {};
  for (const act of acts ?? []) {
    if (!act.is_included) continue;
    if (act.quantity_limit == null && !act.time_period && !act.notes) continue;
    out[String(act.id)] = {
      quantity: act.quantity_limit != null ? String(act.quantity_limit) : "",
      period: (act.time_period as PackageActLimit["period"]) ?? "",
      notes: act.notes ?? "",
    };
  }
  return out;
}

const initialFormState: PackageFormState = {
  hospitalId: "",
  companyId: "",
  name: "",
  monthlyRate: "1",
  periodStart: "",
  periodEnd: "",
  description: "",
  status: "draft",
};

function normalizeStatus(status: string): PackageFormState["status"] {
  if (status === "active" || status === "suspended" || status === "draft") return status;
  return "draft";
}

function PackageFormPage({ mode }: { mode: "create" | "edit" }) {
  const { t } = useTranslation();
  const labels = t.safecheck.packages.create;
  const { showToast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const packageId = mode === "edit" ? Number(params.id) : null;

  const [step, setStep] = useState<Step>(0);
  const [form, setForm] = useState<PackageFormState>(initialFormState);
  const [showValidation, setShowValidation] = useState(false);
  const [actSearch, setActSearch] = useState("");
  const [includedActIds, setIncludedActIds] = useState<string[]>([]);
  const [actLimits, setActLimits] = useState<Record<string, PackageActLimit>>({});
  const [meta, setMeta] = useState<AdminPackageMeta>({ hospitals: [], companies: [] });
  const [acts, setActs] = useState<AdminPackageAct[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingPackage, setLoadingPackage] = useState(mode === "edit");
  const [loadingActs, setLoadingActs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hospitalLabelOverride, setHospitalLabelOverride] = useState("");
  const [companyLabelOverride, setCompanyLabelOverride] = useState("");
  const skipHospitalActsReset = useRef(mode === "edit");

  const steps = ["information", "includedActs", "validation"] as const;
  const pageTitle = mode === "edit" ? labels.editTitle : labels.title;
  const submitLabel = mode === "edit" ? labels.editSubmit : labels.submit;

  const updateForm = <K extends keyof PackageFormState>(key: K, value: PackageFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      setLoadingMeta(false);
      setLoadingPackage(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const formMeta = await fetchAdminPackagesMeta();
        if (!cancelled) setMeta(formMeta);

        if (mode === "edit" && packageId && !Number.isNaN(packageId)) {
          const pkg = await fetchAdminPackage(packageId);
          if (cancelled) return;
          setForm({
            hospitalId: String(pkg.hospital_id),
            companyId: String(pkg.company_id),
            name: pkg.name,
            monthlyRate: String(pkg.monthly_rate ?? ""),
            periodStart: pkg.start_date || pkg.period_start || "",
            periodEnd: pkg.end_date || pkg.period_end || "",
            description: pkg.description || "",
            status: normalizeStatus(pkg.status),
          });
          setHospitalLabelOverride(pkg.hospital || "");
          setCompanyLabelOverride(pkg.company || "");
          setActs(
            (pkg.acts ?? []).map((act) => ({
              id: act.id,
              code: act.code,
              label: act.label,
              cost: act.cost,
            })),
          );
          setIncludedActIds((pkg.included_act_ids ?? []).map(String));
          setActLimits(limitsFromPackageActs(pkg.acts));
          skipHospitalActsReset.current = true;
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger le formulaire.");
        }
      } finally {
        if (!cancelled) {
          setLoadingMeta(false);
          setLoadingPackage(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, packageId]);

  useEffect(() => {
    if (mode === "edit") return;

    const hospitalId = Number(form.hospitalId);
    if (!hospitalId || Number.isNaN(hospitalId) || !getAuthToken()) {
      setActs([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      setLoadingActs(true);
      try {
        const hospitalActs = await fetchAdminPackageHospitalActs(hospitalId);
        if (cancelled) return;
        setActs(hospitalActs);
        if (skipHospitalActsReset.current) {
          skipHospitalActsReset.current = false;
        } else {
          setIncludedActIds([]);
          setActLimits({});
        }
      } catch (err) {
        if (!cancelled) {
          setActs([]);
          setError(err instanceof Error ? err.message : "Impossible de charger les actes.");
        }
      } finally {
        if (!cancelled) setLoadingActs(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form.hospitalId, mode]);

  const canProceedStep0 =
    form.hospitalId &&
    form.companyId &&
    form.name.trim() &&
    form.periodStart &&
    form.periodEnd;

  const hospitalLabel =
    meta.hospitals.find((h) => String(h.id) === form.hospitalId)?.name ||
    hospitalLabelOverride ||
    form.hospitalId;
  const companyLabel =
    meta.companies.find((c) => String(c.id) === form.companyId)?.name ||
    companyLabelOverride ||
    form.companyId;

  const mappedActs = useMemo(
    () =>
      acts.map((act) => ({
        id: String(act.id),
        code: act.code,
        label: act.label,
        costUsd:
          act.cost != null
            ? act.cost.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : undefined,
      })),
    [acts],
  );

  const handleNext = () => {
    if (step === 0) {
      if (!canProceedStep0) {
        setShowValidation(true);
        return;
      }
      setShowValidation(false);
      setStep(1);
      return;
    }
    if (step === 1) {
      setStep(2);
    }
  };

  const handlePrevious = () => {
    setStep((current) => Math.max(0, current - 1) as Step);
  };

  const handleSubmit = async () => {
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const includedIds = includedActIds.map(Number).filter((id) => id > 0);
      const limits = buildLimitsPayload(includedActIds, actLimits);
      const monthlyRate = Number(form.monthlyRate) > 0 ? Number(form.monthlyRate) : 1;
      if (mode === "edit" && packageId) {
        await updateAdminPackage(packageId, {
          package_name: form.name.trim(),
          monthly_rate: monthlyRate,
          start_date: form.periodStart,
          end_date: form.periodEnd,
          description: form.description,
          status: form.status,
          included_act_ids: includedIds,
          limits,
        });
      } else {
        await createAdminPackage({
          package_name: form.name.trim(),
          hospital_id: Number(form.hospitalId),
          company_id: Number(form.companyId),
          monthly_rate: monthlyRate,
          start_date: form.periodStart,
          end_date: form.periodEnd,
          description: form.description,
          status: form.status,
          included_act_ids: includedIds,
          limits,
        });
      }
      showToast(t.actions.saved);
      navigate("/dashboard/packages");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : mode === "edit"
              ? "Mise à jour impossible."
              : "Création impossible.";
      setError(message);
      showToast(message);
    } finally {
      setSaving(false);
    }
  };

  if (loadingMeta || loadingPackage) {
    return <p className="text-sm text-muted">Chargement…</p>;
  }

  if (mode === "edit" && (!packageId || Number.isNaN(packageId))) {
    return (
      <div className="theme-card p-6 text-sm text-muted-foreground">
        Package introuvable.
        <Link to="/dashboard/packages" className="ml-2 text-primary hover:underline">
          {labels.back}
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.packages.title, href: "/dashboard/packages" },
          { label: pageTitle },
        ]}
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/packages"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={14} />
          {labels.back}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-pink/30 bg-pink-soft px-4 py-3 text-sm text-pink">
          {error}
        </div>
      )}

      <div className="theme-card overflow-hidden">
        <div className="border-b border-border px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {steps.map((key, index) => {
              const active = step === index;
              const done = step > index;
              return (
                <div key={key} className="flex items-center gap-2">
                  <div
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold",
                      active || done ? "bg-primary text-white" : "bg-card-muted text-muted",
                    )}
                  >
                    {done ? <Check size={14} /> : index + 1}
                  </div>
                  <span
                    className={cn(
                      "text-sm",
                      active ? "font-semibold text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {labels.steps[key]}
                  </span>
                  {index < steps.length - 1 && (
                    <ChevronRight size={14} className="mx-1 text-muted" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-6 px-6 py-6">
          {step === 0 && (
            <>
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
                <Info size={16} className="text-primary" />
                {labels.generalInfo}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label={labels.fields.hospital} required>
                  <select
                    value={form.hospitalId}
                    onChange={(event) => updateForm("hospitalId", event.target.value)}
                    className={selectClass}
                    disabled={mode === "edit"}
                  >
                    <option value="">{labels.placeholders.hospital}</option>
                    {meta.hospitals.map((hospital) => (
                      <option key={hospital.id} value={String(hospital.id)}>
                        {hospital.name}
                      </option>
                    ))}
                  </select>
                  {showValidation && !form.hospitalId && (
                    <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
                  )}
                </FormField>

                <FormField label={labels.fields.company} required>
                  <select
                    value={form.companyId}
                    onChange={(event) => updateForm("companyId", event.target.value)}
                    className={selectClass}
                    disabled={mode === "edit"}
                  >
                    <option value="">{labels.placeholders.company}</option>
                    {meta.companies.map((company) => (
                      <option key={company.id} value={String(company.id)}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  {showValidation && !form.companyId && (
                    <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
                  )}
                </FormField>

                <FormField label={labels.fields.name} required className="md:col-span-2">
                  <input
                    value={form.name}
                    onChange={(event) => updateForm("name", event.target.value)}
                    placeholder={labels.placeholders.name}
                    className={inputClass}
                  />
                  {showValidation && !form.name.trim() && (
                    <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
                  )}
                </FormField>

                <FormField label={labels.fields.status}>
                  <select
                    value={form.status}
                    onChange={(event) =>
                      updateForm("status", event.target.value as PackageFormState["status"])
                    }
                    className={selectClass}
                  >
                    <option value="draft">{labels.statuses.draft}</option>
                    <option value="active">{labels.statuses.active}</option>
                    <option value="suspended">{labels.statuses.suspended}</option>
                  </select>
                </FormField>

                <FormField label={labels.fields.periodStart} required>
                  <div className="relative">
                    <CalendarDays
                      size={14}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                    />
                    <input
                      type="date"
                      value={form.periodStart}
                      onChange={(event) => updateForm("periodStart", event.target.value)}
                      className={`${inputClass} pl-9`}
                    />
                  </div>
                  {showValidation && !form.periodStart && (
                    <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
                  )}
                </FormField>

                <FormField label={labels.fields.periodEnd} required>
                  <div className="relative">
                    <CalendarDays
                      size={14}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                    />
                    <input
                      type="date"
                      value={form.periodEnd}
                      onChange={(event) => updateForm("periodEnd", event.target.value)}
                      className={`${inputClass} pl-9`}
                    />
                  </div>
                  {showValidation && !form.periodEnd && (
                    <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
                  )}
                </FormField>

                <FormField label={labels.fields.description} className="md:col-span-2">
                  <textarea
                    value={form.description}
                    onChange={(event) => updateForm("description", event.target.value)}
                    placeholder={labels.placeholders.description}
                    className={textareaClass}
                  />
                </FormField>
              </div>
            </>
          )}

          {step === 1 && (
            <PackageIncludedActsStep
              acts={mappedActs}
              labels={labels.includedActs}
              search={actSearch}
              onSearchChange={setActSearch}
              includedActIds={includedActIds}
              onIncludedActIdsChange={setIncludedActIds}
              actLimits={actLimits}
              onActLimitsChange={setActLimits}
              loading={loadingActs}
            />
          )}

          {step === 2 && (
            <PackageValidationStep
              labels={labels.validation}
              statusLabels={labels.statuses}
              hospital={hospitalLabel}
              company={companyLabel}
              name={form.name}
              periodStart={form.periodStart}
              periodEnd={form.periodEnd}
              status={form.status}
              includedActIds={includedActIds}
              totalActs={acts.length}
            />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-4">
          {step === 0 ? (
            <Link
              to="/dashboard/packages"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <ChevronLeft size={16} />
              {labels.cancel}
            </Link>
          ) : (
            <button
              type="button"
              onClick={handlePrevious}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <ChevronLeft size={16} />
              {labels.back}
            </button>
          )}

          {step < 2 ? (
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
            >
              {step === 0 ? labels.nextActs : labels.nextValidation}
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSubmit()}
              className="inline-flex items-center gap-2 rounded-lg bg-green px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-green/90 disabled:opacity-60"
            >
              <Check size={16} />
              {saving ? "…" : submitLabel}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export function CreatePackagePage() {
  return <PackageFormPage mode="create" />;
}

export function EditPackagePage() {
  return <PackageFormPage mode="edit" />;
}
